import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import styles from "./ProductDetail.module.css";
import { MainLayout } from "@/layouts/MainLayout";
import { useAuth } from "@/hooks";
import { productsApi } from "@/api/products.api";
import type { ProductDocument } from "@/types/products.types";
import {
  CoinsIcon,
  ArrowRightIcon,
} from "@/components/icons";
import { agentApi } from "@/api/agent.api";
import {
  CheckIcon,
  Copy,
  Download,
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  File as FileIcon,
  FolderOpen,
} from "lucide-react";
import { CreateUtmLinkModal } from "@/components/CreateUtmLinkModal";

type BaseContent = string[];
type FaqContent = { question: string; answer: string; imageUrls?: string[] }[];
type TncContent = {
  general: string[];
  dos: string[];
  donts: string[];
};

type SectionData = {
  title: string;
  subtitle: string;
  items: BaseContent | FaqContent | TncContent;
};

type UIProductData = {
  id: string;
  name: string;
  category: string;
  imageUrl?: string;
  isPanIndia: boolean;
  stats: {
    payout: string;
  };
  details: {
    benefits: SectionData;
    sell: SectionData;
    req: SectionData;
    tnc: SectionData;
    faq: SectionData;
  };
  documents: ProductDocument[];
};

const CheckListItem = ({ text }: { text: string }) => (
  <li className={styles.gridItem}>
    <div className={styles.checkIcon}>✓</div>
    <span className={styles.listText}>{text}</span>
  </li>
);

// Pick an icon that matches the file type so the list reads at a glance.
const getDocIcon = (filename: string) => {
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  if (["png", "jpg", "jpeg", "webp", "gif", "svg"].includes(ext))
    return <ImageIcon size={22} />;
  if (["xls", "xlsx", "csv"].includes(ext))
    return <FileSpreadsheet size={22} />;
  if (["pdf", "doc", "docx", "txt", "ppt", "pptx"].includes(ext))
    return <FileText size={22} />;
  return <FileIcon size={22} />;
};

const formatDocSize = (bytes?: number | null): string => {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AccordionItem = ({
  question,
  answer,
  imageUrls,
}: {
  question: string;
  answer: string;
  imageUrls?: string[];
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className={styles.accordionItem} data-open={isOpen}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={styles.accordionHeader}
      >
        {question}
        <span className={styles.accordionIcon}>{isOpen ? "−" : "+"}</span>
      </button>
      {isOpen && (
        <div className={styles.accordionBody}>
          <p>{answer}</p>
          {imageUrls && imageUrls.length > 0 && (
            <div className={styles.faqImagesWrapper}>
              {imageUrls.map((url, index) => (
                <div key={index} className={styles.faqImageContainer}>
                  <img
                    src={url}
                    alt={`FAQ Visual ${index + 1}`}
                    className={styles.faqImage}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const ProductDetail: React.FC = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  // Sub-agent view support
  const agentIdOverride = searchParams.get("agentId") || null;
  const agentNameOverride = searchParams.get("agentName") || null;

  const [product, setProduct] = useState<UIProductData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [agentId, setAgentId] = useState<string | null>(null);
  const [isAgentLoading, setIsAgentLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const [linkMode, setLinkMode] = useState<"landing" | "utm">("landing");
  const [affiliateCode, setAffiliateCode] = useState<string>("");
  const [utmModalProduct, setUtmModalProduct] = useState<{
    id: string;
    name: string;
    category: string;
  } | null>(null);

  const [pincode, setPincode] = useState("");
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [eligibilityMsg, setEligibilityMsg] = useState<{
    type: "success" | "error";
    msg: string;
  } | null>(null);

  const [activeMainTab, setActiveMainTab] = useState<"policy">("policy");

  const [activeSubTab, setActiveSubTab] =
    useState<keyof UIProductData["details"]>("benefits");

  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  // Fetch a short-lived signed URL, then trigger a browser download.
  const handleDownloadDoc = async (doc: ProductDocument) => {
    if (!productId) return;
    setDownloadingDocId(doc.id);
    try {
      const result = await productsApi.getDocumentUrl(productId, doc.id);
      if (result.status === "Success" && result.response.url) {
        const link = document.createElement("a");
        link.href = result.response.url;
        link.download = result.response.filename || doc.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setError(result.message || "Could not download document.");
      }
    } catch {
      setError("Could not download document.");
    } finally {
      setDownloadingDocId(null);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) navigate("/agent-login");
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetchAgentDetails = async () => {
      setIsAgentLoading(true);
      
      // If viewing as sub-agent, use the override for agentId but still
      // fetch the sub-agent's affiliate_code so UTM links work here too.
      if (agentIdOverride) {
        setAgentId(agentIdOverride);
        try {
          const response = await agentApi.getAgentDetails({
            query_type: "get_agent_details",
            agent_id: agentIdOverride,
          });
          const data =
            Array.isArray(response.response) && response.response.length > 0
              ? response.response[0]
              : null;
          if (data?.affiliate_code) setAffiliateCode(String(data.affiliate_code));
        } catch (err) {
          console.error("Failed to fetch sub-agent affiliate_code", err);
        } finally {
          setIsAgentLoading(false);
        }
        return;
      }

      try {
        const storedAgentId =
          localStorage.getItem("agent_id") || localStorage.getItem("agentId");

        if (!storedAgentId) {
          console.warn("Agent ID not found in storage");
          setIsAgentLoading(false);
          return;
        }

        const response = await agentApi.getAgentDetails({
          query_type: "get_agent_details",
          agent_id: storedAgentId,
        });

        const isSuccess =
          (response.status === "Success" || response.message === "Success") &&
          response.response &&
          Array.isArray(response.response) &&
          response.response.length > 0;

        if (isSuccess) {
          const data = response.response[0];
          setAgentId(String(data.agent_id));
          if (data.affiliate_code) setAffiliateCode(String(data.affiliate_code));
        }
      } catch (err) {
        console.error("Failed to fetch agent details", err);
      } finally {
        setIsAgentLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchAgentDetails();
    }
  }, [isAuthenticated, agentIdOverride]);

  const handleSellNow = () => {
    if (!agentId || !productId) {
      alert("Agent details are still loading. Please try again in a moment.");
      return;
    }

    // UTM mode: open the Create UTM Link modal defaulted to this product.
    if (linkMode === "utm") {
      setUtmModalProduct({
        id: productId,
        name: product?.name ?? "",
        category: product?.category ?? "",
      });
      return;
    }

    const compositeKey = `${agentId}:${productId}`;
    const encodedRef = btoa(compositeKey);

    // Use config for proper dev/prod URL switching
    const { getExternalURL } = require("@/config");
    const baseUrl = getExternalURL("CUSTOMER_PORTAL");

    const targetUrl = `${baseUrl}/login?ref=${encodedRef}&next=/product/${productId}/apply`;

    navigator.clipboard.writeText(targetUrl);

    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const transformApiProductToUI = (apiData: any): UIProductData => {
    const content = apiData.content || {};

    return {
      id: apiData.id,
      name: apiData.name,
      category: apiData.category,
      imageUrl: apiData.imageUrl,
      isPanIndia: apiData.isPanIndia,
      stats: {
        payout: apiData.payoutPercentage || "",
      },
      details: {
        benefits: {
          title: "Key Benefits",
          subtitle: "Why customers love this product",
          items: content.benefits || [],
        },
        sell: {
          title: "Target Audience",
          subtitle: "Who should you sell this to?",
          items: content.whomToSell || [],
        },
        req: {
          title: "Requirements",
          subtitle: "Mandatory conditions for approval",
          items: content.requirements || [],
        },
        tnc: {
          title: "Terms & Conditions",
          subtitle: "Important guidelines to follow",
          items: {
            general: content.terms || [],
            dos: content.dos || [],
            donts: content.donts || [],
          },
        },
        faq: {
          title: "Frequently Asked Questions",
          subtitle: "Common doubts clarified",
          items: (content.faq || []).map((f: any) => ({
            question: f.q,
            answer: f.a,
            imageUrls: f.imageUrls,
          })),
        },
      },
      documents: Array.isArray(apiData.documents) ? apiData.documents : [],
    };
  };

  const fetchProductDetails = useCallback(async () => {
    if (!productId) return;

    setLoading(true);
    setError("");

    try {
      const result = await productsApi.getProducts({ channel: "AGENT" });

      if (result.status === "Success" && Array.isArray(result.response)) {
        const foundProduct = result.response.find(
          (p: any) => p.id === productId,
        );

        if (foundProduct) {
          const formatted = transformApiProductToUI(foundProduct);
          setProduct(formatted);
        } else {
          setError("Product not found in catalog.");
        }
      } else {
        setError(result.message || "Failed to fetch product details.");
      }
    } catch (err) {
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchProductDetails();
  }, [fetchProductDetails]);

  const handleCheckEligibility = async () => {
    if (!pincode || pincode.length !== 6) return;

    setCheckingEligibility(true);
    setEligibilityMsg(null);

    const result = await productsApi.checkEligibility({ pincode });

    if (result.status === "Success" && result.response) {
      const eligibleProducts = result.response.eligible_products || [];
      const isEligible = eligibleProducts.some((p: any) => p.id === productId);

      if (isEligible) {
        setEligibilityMsg({
          type: "success",
          msg: "Service available in this pincode!",
        });
      } else {
        setEligibilityMsg({
          type: "error",
          msg: "Service NOT available in this area.",
        });
      }
    } else {
      setEligibilityMsg({
        type: "error",
        msg: result.message || "Check failed",
      });
    }
    setCheckingEligibility(false);
  };

  const renderDetailsContent = () => {
    if (!product) return null;
    const sectionData = product.details[activeSubTab];
    if (!sectionData) return null;

    return (
      <div className={styles.contentArea}>
        <h3 className={styles.contentTitle}>{sectionData.title}</h3>
        <p className={styles.contentDesc}>{sectionData.subtitle}</p>

        {activeSubTab === "faq" && (
          <div className={styles.accordionContainer}>
            {(sectionData.items as FaqContent).map((faq, idx) => (
              <AccordionItem
                key={idx}
                question={faq.question}
                answer={faq.answer}
                imageUrls={faq.imageUrls}
              />
            ))}
            {(sectionData.items as FaqContent).length === 0 && (
              <p className="text-gray-500">No FAQs available.</p>
            )}
          </div>
        )}

        {activeSubTab === "tnc" && (
          <>
            <ol className={styles.tncList}>
              {(sectionData.items as TncContent).general.map((item, idx) => (
                <li key={idx} className={styles.tncItem}>
                  <span className={styles.tncIndex}>{idx + 1}.</span>
                  <span>{item}</span>
                </li>
              ))}
            </ol>

            <div className={styles.dosDontGrid}>
              <div className={styles.ddColumn}>
                <div className={`${styles.ddHeader} ${styles.dosHeader}`}>
                  Do's
                </div>
                <div className={`${styles.ddCard} ${styles.dosCard}`}>
                  <ul className={styles.ddList}>
                    {(sectionData.items as TncContent).dos.map((item, idx) => (
                      <li key={idx} className={styles.ddItem}>
                        <span className={`${styles.ddIcon} ${styles.dosIcon}`}>
                          ✓
                        </span>
                        {item}
                      </li>
                    ))}
                    {(sectionData.items as TncContent).dos.length === 0 && (
                      <li className={styles.ddItem}>No specific Do's</li>
                    )}
                  </ul>
                </div>
              </div>

              <div className={styles.ddColumn}>
                <div className={`${styles.ddHeader} ${styles.dontHeader}`}>
                  Don'ts
                </div>
                <div className={`${styles.ddCard} ${styles.dontCard}`}>
                  <ul className={styles.ddList}>
                    {(sectionData.items as TncContent).donts.map(
                      (item, idx) => (
                        <li key={idx} className={styles.ddItem}>
                          <span
                            className={`${styles.ddIcon} ${styles.dontIcon}`}
                          >
                            ✕
                          </span>
                          {item}
                        </li>
                      ),
                    )}
                    {(sectionData.items as TncContent).donts.length === 0 && (
                      <li className={styles.ddItem}>No specific Don'ts</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}

        {activeSubTab !== "faq" && activeSubTab !== "tnc" && (
          <ul className={styles.gridList}>
            {(sectionData.items as BaseContent).map((item, idx) => (
              <CheckListItem key={idx} text={item} />
            ))}
            {(sectionData.items as BaseContent).length === 0 && (
              <p className="text-gray-500">No information available.</p>
            )}
          </ul>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <div className={styles.pageContainer}>
          <div className="flex justify-center items-center h-[60vh]">
            <div className="animate-pulse text-xl text-blue-900 font-semibold">
              Loading Details...
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !product) {
    return (
      <MainLayout>
        <div className={styles.pageContainer}>
          <div className="flex flex-col justify-center items-center h-[60vh] gap-4">
            <div className="text-red-500 text-lg font-medium">
              {error || "Product not found"}
            </div>
            <button
              onClick={() => navigate("/products")}
              className={styles.backBtn}
            >
              Back to Products
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className={styles.pageContainer}>
        <div className={styles.topNav}>
          <button
            onClick={() => navigate(`/products${agentIdOverride ? `?agentId=${agentIdOverride}${agentNameOverride ? `&agentName=${encodeURIComponent(agentNameOverride)}` : ''}` : ''}`)}
            className={styles.backBtn}
          >
            <ArrowRightIcon size={14} className={styles.rotateIcon} />
            <span>Back to Products</span>
          </button>
        </div>

        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {product.imageUrl && (
              <img
                src={product.imageUrl}
                alt={`${product.name} Logo`}
                className={styles.productLogo}
              />
            )}
            <div className={styles.headerText}>
              <span className={styles.categoryTag}>{product.category}</span>
              <h1 className={styles.title}>{product.name}</h1>
              <p className={styles.subtitle}>ID: {product.id}</p>
              {agentNameOverride && (
                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                  Acting as: {agentNameOverride} (ID: {agentIdOverride})
                </p>
              )}
            </div>
          </div>

          <div className={styles.headerRight}>
            <div className={styles.shareContainer}>
              <span className={styles.shareLabel}>Share as:</span>
              <div className={styles.shareToggleGroup}>
                {(["landing", "utm"] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setLinkMode(mode)}
                    className={`${styles.shareToggleBtn} ${
                      linkMode === mode ? styles.shareToggleActive : ""
                    }`}
                  >
                    {mode === "landing" ? "Landing Page" : "UTM Link"}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.headerControlsRow}>
              <button
                onClick={handleSellNow}
                disabled={isAgentLoading || !agentId}
                className={styles.applyNowBtn}
              >
                {isAgentLoading ? (
                  <span>Loading...</span>
                ) : linkCopied ? (
                  <>
                    <span>Link Copied</span>
                    <CheckIcon size={16} />
                  </>
                ) : (
                  <>
                    <span>Sell Now</span>
                    <Copy size={16} />
                  </>
                )}
              </button>

              <div className={styles.eligibilityWidget}>
                <label className={styles.checkLabel}>Check Eligibility</label>
                <div className={styles.checkInputGroup}>
                <input
                  type="text"
                  placeholder="Enter Pincode"
                  className={styles.pincodeInput}
                  value={pincode}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val) && val.length <= 6) setPincode(val);
                  }}
                />
                <button
                  className={styles.checkBtn}
                  onClick={handleCheckEligibility}
                  disabled={checkingEligibility}
                >
                  {checkingEligibility ? "..." : "Check"}
                </button>
              </div>
                {eligibilityMsg && (
                  <div
                    className={`text-xs mt-2 font-medium ${eligibilityMsg.type === "success"
                        ? "text-green-600"
                        : "text-red-500"
                      }`}
                  >
                    {eligibilityMsg.msg}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.statsGrid}>
          <div className={styles.modernCard}>
            <div className={styles.cardContent}>
              <h4>Payout</h4>
              <div className={styles.cardValue}>{product.stats.payout}</div>
            </div>
            <div className={`${styles.cardIconBox} ${styles.greenBox}`}>
              <CoinsIcon size={32} />
            </div>
          </div>
        </div>

        <div className={styles.tabsContainer}>
          <div className={styles.mainTabsHeader}>
            {["policy"].map((tab) => (
              <button
                key={tab}
                className={`${styles.tabBtn} ${activeMainTab === tab ? styles.activeTab : ""
                  }`}
                onClick={() => setActiveMainTab(tab as any)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className={styles.tabBody}>
            {activeMainTab === "policy" && (
              <>
                <div className={styles.subTabsHeader}>
                  {[
                    { id: "benefits", label: "Benefits" },
                    { id: "sell", label: "Whom to Sell?" },
                    { id: "req", label: "Requirements" },
                    { id: "tnc", label: "T&C" },
                    { id: "faq", label: "FAQs" },
                  ].map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => setActiveSubTab(sub.id as any)}
                      className={`${styles.subTabBtn} ${activeSubTab === sub.id ? styles.activeSubTab : ""
                        }`}
                    >
                      {sub.label}
                    </button>
                  ))}
                </div>
                {renderDetailsContent()}
              </>
            )}
          </div>
        </div>

        {product.documents.length > 0 && (
          <div className={styles.docSection}>
            <div className={styles.docHeader}>
              <div className={styles.docHeaderIcon}>
                <FolderOpen size={26} />
              </div>
              <div className={styles.docHeaderText}>
                <h3>Documents</h3>
                <p>
                  Download eligibility criteria, pincode lists, journeys and
                  other resources for this product.
                </p>
              </div>
            </div>

            <div className={styles.docGrid}>
              {product.documents.map((doc) => (
                <div key={doc.id} className={styles.docCard}>
                  <div className={styles.docIcon}>{getDocIcon(doc.filename)}</div>
                  <div className={styles.docInfo}>
                    <div className={styles.docLabel}>
                      {doc.label || doc.filename}
                    </div>
                    <div className={styles.docMeta}>
                      <span className={styles.docTypeTag}>{doc.type}</span>
                      <span className={styles.docFilename}>{doc.filename}</span>
                      {formatDocSize(doc.sizeBytes) && (
                        <span>· {formatDocSize(doc.sizeBytes)}</span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={downloadingDocId === doc.id}
                    onClick={() => handleDownloadDoc(doc)}
                    className={styles.docDownloadBtn}
                  >
                    {downloadingDocId === doc.id ? (
                      <>
                        <span className={styles.docSpinner} /> Preparing…
                      </>
                    ) : (
                      <>
                        <Download size={16} /> Download
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {utmModalProduct && (
        <CreateUtmLinkModal
          affiliateCode={affiliateCode}
          product={utmModalProduct}
          onClose={() => setUtmModalProduct(null)}
        />
      )}
    </MainLayout>
  );
};
